import * as React from 'react';
import {Filter, FilterType} from '../CommonInterfaces';
import DropDown, {DropDownColor, DropDownOption} from '../DropDown';
import TypeAhead, {TypeAheadOption} from '../TypeAhead';

interface FilterInputRendererProps {
  selectedFilter: Filter;
  filterValue: string;
  startDate: string;
  endDate: string;
  parentFilterKey: string;
  onFilterValueChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onInputKeyUp: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  onChangeAndApplyFilter: (option: DropDownOption) => void;
  onTypeAheadChangeAndApply: (value: string) => void;
  onTypeAheadFilterChange: (query: string) => void;
  onChangeAndApplyRadio: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onStartDateChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onEndDateChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onApplyFilter: () => void;
  onResetFilter: () => void;
  getFilterSearchType: (filter: Filter) => string;
}

const FilterInputRenderer: React.FC<FilterInputRendererProps> = ({
  selectedFilter,
  filterValue,
  startDate,
  endDate,
  parentFilterKey,
  onFilterValueChange,
  onInputKeyUp,
  onChangeAndApplyFilter,
  onTypeAheadChangeAndApply,
  onTypeAheadFilterChange,
  onChangeAndApplyRadio,
  onStartDateChange,
  onEndDateChange,
  onApplyFilter,
  onResetFilter,
  getFilterSearchType
}) => {
  if (!selectedFilter) return null;

  const submitCancel = (
    <>
      <a className="submit-btn icon" data-testid="check-button" onClick={onApplyFilter}>
        <i className="fa fa-check" aria-hidden="true"></i>
      </a>
      <a className="text-danger icon" data-testid="cancel-button" onClick={onResetFilter}>
        <i className="fa fa-times" aria-hidden="true"></i>
      </a>
    </>
  );

  switch (selectedFilter.type) {
    case FilterType.Dropdown: {
      const options: DropDownOption[] = (selectedFilter.options || []).map((o) => ({
        name: o.displayName,
        value: `${o.value}`
      }));
      return (
        <>
          <li id="filter-dropdown" data-testid="filter-dropdown">
            <DropDown
              options={options}
              prefixText={selectedFilter.placeHolder}
              selected={selectedFilter.placeHolder ? null : options[0]}
              onSelect={onChangeAndApplyFilter}
              color={DropDownColor.white}
            />
          </li>
          {selectedFilter.key !== parentFilterKey && (
            <li>
              <a className="text-danger" onClick={onResetFilter}>
                Cancel
              </a>
            </li>
          )}
        </>
      );
    }
    case FilterType.String:
      return (
        <>
          <li className="input-with-label">
            <label className="custom-label">{getFilterSearchType(selectedFilter)}</label>
            <input
              className="custom-input"
              type="text"
              value={filterValue}
              onChange={onFilterValueChange}
              onKeyUp={onInputKeyUp}
              data-testid="input-filter"
            />
          </li>
          <li>
            <a id="add-filter" data-testid="add-filter" onClick={onApplyFilter}>
              Add
            </a>
          </li>
          <li>
            <a className="text-danger" onClick={onResetFilter}>
              Cancel
            </a>
          </li>
        </>
      );
    case FilterType.Date:
      return (
        <div className="filter-input-div">
          <input
            name={selectedFilter.key}
            value={filterValue}
            onChange={onFilterValueChange}
            onKeyUp={onInputKeyUp}
            className="form-control filter-input"
            type="date"
            data-testid="filter-input"
            date-format="yyyy-mm-dd"
          />
          {submitCancel}
        </div>
      );
    case FilterType.TypeAhead: {
      const baseOptions: TypeAheadOption[] = (selectedFilter.options || []).map((o) => ({
        value: o.value.toString(),
        displayName: o.displayName
      }));
      const filterVal = filterValue === selectedFilter.key ? '' : filterValue;
      const typeAheadOptions = filterVal
        ? baseOptions.filter((o) => o.displayName.toLowerCase().includes(filterValue.toLowerCase()))
        : baseOptions;
      return (
        <>
          <div className="btn-group">
            <li id="filter-dropdown">
              <TypeAhead
                name={selectedFilter.displayName}
                placeholder={selectedFilter.placeHolder || ''}
                value={filterVal}
                options={typeAheadOptions}
                onRequestOptions={onTypeAheadFilterChange}
                onSelect={onTypeAheadChangeAndApply}
                dropdownCss="typeahead-dropdown-menu"
              />
            </li>
          </div>
          <li>
            <a id="add-filter" onClick={onApplyFilter}>
              Submit
            </a>
          </li>
          <li>
            <a className="text-danger" onClick={onResetFilter}>
              Cancel
            </a>
          </li>
        </>
      );
    }
    case FilterType.Radio: {
      let key = 0;
      return (
        <li className="form-group filter-input">
          <div className="rbtn-group">
            {(selectedFilter.options || []).map((option, index) => [
              <input
                key={key++}
                className="filter-radio"
                type="radio"
                name={selectedFilter.key}
                id={selectedFilter.key + '_' + index}
                value={option.value.toString()}
                onChange={onChangeAndApplyRadio}
              />,
              <label key={key++} className="filter-radio-label" htmlFor={selectedFilter.key + '_' + index}>
                {option.displayName}
              </label>
            ])}
          </div>
        </li>
      );
    }
    case FilterType.Range:
      return (
        <>
          <li>{selectedFilter.displayName}:</li>
          <li>
            <input
              name="startDate"
              value={startDate}
              onChange={onStartDateChange}
              onKeyUp={onInputKeyUp}
              className="form-control filter-input"
              type="date"
              placeholder="Start Date"
              data-testid="filter-input-start-date"
              date-format="yyyy-mm-dd"
            />
          </li>
          <li>
            <input
              name="endDate"
              value={endDate}
              onChange={onEndDateChange}
              onKeyUp={onInputKeyUp}
              className="form-control filter-input"
              type="date"
              placeholder="End Date"
              data-testid="filter-input-end-date"
              date-format="yyyy-mm-dd"
            />
          </li>
          <li>
            <a id="add-filter" onClick={onApplyFilter}>
              Add
            </a>
          </li>
          <li>
            <a className="text-danger" onClick={onResetFilter}>
              Cancel
            </a>
          </li>
        </>
      );
    default:
      return null;
  }
};

export default FilterInputRenderer;
